import { useLazyContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { EmailIntegrationsContext } from "@redotech/redo-merchant-support-app/services/email-integrations";
import { ConversationPlatform } from "@redotech/redo-model/conversation";
import { SettingsPermissions } from "@redotech/redo-model/permissions";
import { EmailInfo } from "@redotech/redo-model/team";
import { AssignToAgentState } from "@redotech/redo-return-ui/flows/chat-flow/assign-to-agent";
import { containsImg } from "@redotech/redo-return-ui/flows/chat-flow/step-rich-text-util";
import { StepTypeDetailsProps } from "@redotech/redo-return-ui/flows/step";
import { CollapseSection } from "@redotech/redo-web/card";
import { LabeledInput } from "@redotech/redo-web/labeled-input";
import { SelectDropdown } from "@redotech/redo-web/select-dropdown";
import Quill from "quill";
import { memo, useEffect, useRef, useState } from "react";
import { StepRichTextEditor } from "./step-rich-text";

const options: ConversationPlatform[] = [
  ConversationPlatform.EMAIL,
  ConversationPlatform.REDO_CHAT,
];

export const AssignToAgentDetails = memo(function AssignToAgentDetails({
  state,
  setState,
}: StepTypeDetailsProps<AssignToAgentState>) {
  const canEditSupportSettings = useHasPermission(
    SettingsPermissions.SET_SUPPORT_SETTINGS,
  );

  const handleChannelChange = useHandler((channel: ConversationPlatform) => {
    setState((state) => ({ ...state, channel }));
  });
  const handleEmailToSendAsChange = useHandler((emailToSendAs?: EmailInfo) => {
    setState((state) => ({ ...state, emailToSendAs }));
  });
  const [availableEmails, setAvailableEmails] = useState<EmailInfo[]>([]);

  useEffect(() => {
    if (state.channel === ConversationPlatform.EMAIL) {
      if (
        availableEmails.length > 0 &&
        (!state.emailToSendAs || state.emailToSendAs.email === "")
      ) {
        handleEmailToSendAsChange(availableEmails[0]);
      }
    } else if (state.channel === ConversationPlatform.REDO_CHAT) {
      handleEmailToSendAsChange(undefined);
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableEmails, state.emailToSendAs, state.channel, setState]);

  const messageQuillRef = useRef<Quill | undefined>(undefined);
  const emailPromptQuillRef = useRef<Quill | undefined>(undefined);
  const followUpQuillRef = useRef<Quill | undefined>(undefined);

  const [emailIntegrationsLoad] = useLazyContext(EmailIntegrationsContext);

  useEffect(() => {
    if (!emailIntegrationsLoad.value) {
      return;
    }
    appendEmails(
      emailIntegrationsLoad.value.customIntegrations.map((integration) => {
        return {
          email: integration.email,
          name: integration.name,
          integrationKind: "custom",
        };
      }),
    );
    appendEmails(
      emailIntegrationsLoad.value.gmailIntegrations.map((integration) => {
        return {
          email: integration.email,
          name: integration.name,
          integrationKind: "gmail",
        };
      }),
    );
    appendEmails(
      emailIntegrationsLoad.value.outlookIntegrations.map((integration) => {
        return {
          email: integration.email,
          name: integration.name,
          integrationKind: "outlook",
        };
      }),
    );
  }, [emailIntegrationsLoad.value]);

  const appendEmails = (emails: EmailInfo[]) => {
    setAvailableEmails((oldAvailableEmails) => {
      const newEmails = [...oldAvailableEmails];
      for (const email of emails) {
        if (!newEmails.some((existing) => existing.email === email.email)) {
          newEmails.push(email);
        }
      }
      return [...newEmails];
    });
  };

  const handleMessageChange = useHandler(() => {
    if (!messageQuillRef.current) {
      return;
    }
    let message = messageQuillRef.current.getText();
    let messageHtml: string | undefined =
      messageQuillRef.current.getSemanticHTML();
    if (message === "\n") {
      message = "";
    }
    if (messageHtml === "<p></p>") {
      messageHtml = undefined;
    }

    if (messageHtml !== state.messageHtml || message !== state.message) {
      setState((state) => ({ ...state, message, messageHtml }));
    }
  });

  const handleEmailPromptChange = useHandler(() => {
    if (!emailPromptQuillRef.current) {
      return;
    }
    let emailPromptMessage = emailPromptQuillRef.current.getText();
    let emailPromptMessageHtml: string | undefined =
      emailPromptQuillRef.current.getSemanticHTML();
    if (emailPromptMessage === "\n") {
      emailPromptMessage = "";
    }
    if (emailPromptMessageHtml === "<p></p>") {
      emailPromptMessageHtml = undefined;
    }

    if (
      emailPromptMessageHtml !== state.emailPromptMessageHtml ||
      emailPromptMessage !== state.emailPromptMessage
    ) {
      setState((state) => ({
        ...state,
        emailPromptMessage,
        emailPromptMessageHtml,
      }));
    }
  });

  const handleFollowUpChange = useHandler(() => {
    if (!followUpQuillRef.current) {
      return;
    }
    let followUpMessage = followUpQuillRef.current.getText();
    let followUpMessageHtml: string | undefined =
      followUpQuillRef.current.getSemanticHTML();
    if (followUpMessage === "\n") {
      followUpMessage = "";
    }
    if (followUpMessageHtml === "<p></p>") {
      followUpMessageHtml = undefined;
    }

    if (
      followUpMessageHtml !== state.followUpMessageHtml ||
      followUpMessage !== state.followUpMessage
    ) {
      setState((state) => ({ ...state, followUpMessage, followUpMessageHtml }));
    }
  });

  return (
    <>
      {(availableEmails.length || 0) > 0 ? (
        <LabeledInput label="Channel">
          <SelectDropdown
            disabled={!canEditSupportSettings}
            options={options}
            value={state.channel}
            valueChange={handleChannelChange}
          >
            {(option) => option.charAt(0).toUpperCase() + option.slice(1)}
          </SelectDropdown>
        </LabeledInput>
      ) : null}
      {state.channel === ConversationPlatform.EMAIL ? (
        <>
          <LabeledInput label="Email to send as">
            <SelectDropdown
              disabled={!canEditSupportSettings}
              options={availableEmails}
              value={state.emailToSendAs}
              valueChange={handleEmailToSendAsChange}
            >
              {(email) => email.email}
            </SelectDropdown>
          </LabeledInput>
          <CollapseSection
            defaultCollapsed={false}
            title="If customer email is known"
          >
            <LabeledInput
              description="Message to show to the customer letting them know they will be contacted by email"
              errors={
                containsImg(state.messageHtml) ? ["Cannot contain images"] : []
              }
              label="Message"
            >
              <StepRichTextEditor
                htmlValue={state.messageHtml}
                onTextChange={handleMessageChange}
                quillRef={messageQuillRef}
                readOnly={!canEditSupportSettings}
                textValue={state.message}
              />
            </LabeledInput>
          </CollapseSection>
        </>
      ) : (
        <LabeledInput
          description="Message to show to the customer while they wait for customer support agent"
          errors={
            containsImg(state.messageHtml) ? ["Cannot contain images"] : []
          }
          label="Message"
        >
          <StepRichTextEditor
            htmlValue={state.messageHtml}
            onTextChange={handleMessageChange}
            quillRef={messageQuillRef}
            readOnly={!canEditSupportSettings}
            textValue={state.message}
          />
        </LabeledInput>
      )}
      {state.channel === ConversationPlatform.EMAIL ? (
        <CollapseSection
          defaultCollapsed={false}
          title="If customer email is unknown"
        >
          <LabeledInput
            description="If the customer's email hasn't yet been collected in the Chat Flow, this message will be sent first to ask them for one"
            errors={
              containsImg(state.emailPromptMessageHtml)
                ? ["Cannot contain images"]
                : []
            }
            label="Email collection message"
          >
            <StepRichTextEditor
              htmlValue={state.emailPromptMessageHtml}
              onTextChange={handleEmailPromptChange}
              quillRef={emailPromptQuillRef}
              readOnly={!canEditSupportSettings}
              textValue={state.emailPromptMessage}
            />
          </LabeledInput>
          <LabeledInput
            description="Message to show to the customer, after they've entered their email, letting them know they will be contacted by email"
            errors={
              containsImg(state.followUpMessageHtml)
                ? ["Cannot contain images"]
                : []
            }
            label="Follow-up message"
          >
            <StepRichTextEditor
              htmlValue={state.followUpMessageHtml}
              onTextChange={handleFollowUpChange}
              quillRef={followUpQuillRef}
              readOnly={!canEditSupportSettings}
              textValue={state.followUpMessage}
            />
          </LabeledInput>
        </CollapseSection>
      ) : null}
    </>
  );
});
